import React, { useState } from 'react';
import { Row, Col, Card, CardBody, Collapse } from 'reactstrap';
import { Form } from '@availity/form';
import { Input, Select, Switch, FieldArray } from '../../lib/form-field';
import formValidation from './formValidation';

import * as companiesActions from '../../store/companies/actions';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { SubmitButton } from '../Common/Modal/modalActionButtons';
import TIMEZONES from '../../config/timezones';

/**
 * ISO 8601 keys: Monday=1 … Sunday=7, all seven always written. The label is
 * hand-written and not taken from moment: `moment.locale` is process global
 * state, and changing it here would change date formatting across the CRM.
 */
const WEEKDAYS = [
  [1, 'Monday'],
  [2, 'Tuesday'],
  [3, 'Wednesday'],
  [4, 'Thursday'],
  [5, 'Friday'],
  [6, 'Saturday'],
  [7, 'Sunday'],
];

/**
 * What a brand new company sees. The same hours the migration writes onto the
 * existing ones: the default is not a choice, it is a snapshot of how the
 * call center works today.
 *
 * A closed day still carries `start` and `end`: the form flips the switch and
 * keeps the values, so opening on Sunday for a month of campaign does not
 * mean typing them again from memory. And `open` is what holds the day shut,
 * not a missing key — a save with merge would never delete a removed one.
 */
const DEFAULT_CALL_CENTER = {
  callCenter: {
    workingTime: {
      1: { open: true, start: '08:40', end: '19:50' },
      2: { open: true, start: '08:40', end: '19:50' },
      3: { open: true, start: '08:40', end: '19:50' },
      4: { open: true, start: '08:40', end: '19:50' },
      5: { open: true, start: '08:40', end: '19:50' },
      6: { open: true, start: '08:40', end: '17:50' },
      7: { open: false, start: '09:00', end: '13:00' },
    },
    recurringClosedDays: [],
    closedDays: [],
  },
};

const FormCompany = (props) => {
  const editingCompany = props.company || {};

  const facebookAdAccountIds = editingCompany.facebookAdAccountIds || [];
  const tiktokAdAccountIds = editingCompany.tiktokAdAccountIds || [];

  const [active, setActive] = useState(editingCompany.active || false);
  const [facebookModule, setFacebookModule] = useState(
    editingCompany.facebookModule || false,
  );
  const [googleModule, setGoogleModule] = useState(
    editingCompany.googleModule || false,
  );
  const [tiktokModule, setTiktokModule] = useState(
    editingCompany.tiktokModule || false,
  );
  const [supplierModule, setSupplierModule] = useState(
    editingCompany.supplierModule || false,
  );
  const [audienceModule, setAudienceModule] = useState(
    editingCompany.audienceModule || false,
  );
  const [demModule, setDemModule] = useState(editingCompany.demModule || false);
  const [rcsModule, setRcsModule] = useState(editingCompany.rcsModule || false);
  const [aiModule, setAiModule] = useState(editingCompany.aiModule || false);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  // The two closure lists are arrays on Firestore and text in the form, as
  // `negativeReasons` on the acquisition order and `reasons` on the
  // monetization order already are.
  const linesFromArray = (value) =>
    Array.isArray(value) ? value.join('\n') : '';
  const arrayFromLines = (text) =>
    String(text || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

  const handleSubmit = (values) => {
    const companyData = {
      ...values,
      callCenter: {
        ...values.callCenter,
        recurringClosedDays: arrayFromLines(
          values.callCenter.recurringClosedDays,
        ),
        closedDays: arrayFromLines(values.callCenter.closedDays),
      },
      active,
      facebookModule,
      googleModule,
      tiktokModule,
      supplierModule,
      audienceModule,
      demModule,
      rcsModule,
      aiModule,
    };

    dispatch(
      props.create
        ? companiesActions.createCompany(companyData)
        : companiesActions.updateCompany({
            id: editingCompany.id,
            ...companyData,
          }),
    );
    props.closeModal();
  };

  return (
    <Card>
      <CardBody>
        <Form
          initialValues={{
            ...DEFAULT_CALL_CENTER,
            ...editingCompany,
            facebookAdAccountIds,
            tiktokAdAccountIds,
            facebookModule,
            googleModule,
            tiktokModule,
            supplierModule,
            audienceModule,
            aiModule,
            callCenter: {
              ...DEFAULT_CALL_CENTER.callCenter,
              ...editingCompany.callCenter,
              recurringClosedDays: linesFromArray(
                (editingCompany.callCenter || {}).recurringClosedDays,
              ),
              closedDays: linesFromArray(
                (editingCompany.callCenter || {}).closedDays,
              ),
            },
          }}
          validationSchema={formValidation}
          onSubmit={handleSubmit}
        >
          {({ isValid, values }) => (
            <>
              <Row>
                <Col md="3">
                  <Input type="text" name="name" id="name" />
                </Col>
                <Col md="3">
                  <Input
                    id="waitingTimeRecall"
                    name="waitingTimeRecall"
                    label="Waiting Time Recall"
                    min={0.5}
                    type="number"
                    step="0.1"
                  />
                  <p className="font-size-12 text-muted">
                    In hours (ex. 0,5 or 2)
                  </p>
                </Col>
                <Col md="2">
                  <Switch
                    name="active"
                    label="Active?"
                    defaultChecked={active}
                    onClick={() => setActive(!active)}
                  />
                </Col>
              </Row>
              <Row>
                <Col md="3">
                  <Select
                    name="phoneExchange"
                    id="phoneExchange"
                    label="Phone Exchange"
                    isMulti={false}
                    options={[
                      {
                        label: 'None',
                        value: 'none',
                      },
                      {
                        label: 'Restart',
                        value: 'restart',
                      },
                    ]}
                  />
                </Col>
                <Col md="3">
                  <Select
                    name="timezone"
                    id="timezone"
                    label="Timezone"
                    isMulti={false}
                    options={TIMEZONES}
                  />
                </Col>
              </Row>
              <Row className="mt-3 mb-2">
                <Col xs="auto" className="d-flex align-items-center">
                  <h5 className="m-0">{t('Call center opening hours')}</h5>
                </Col>
                <Col>
                  <hr />
                </Col>
              </Row>
              {WEEKDAYS.map(([day, label]) => {
                const isOpen =
                  values.callCenter?.workingTime?.[day]?.open === true;
                return (
                  <Row key={day} className="align-items-start">
                    <Col md="2" className="pt-4">
                      {/*
                        No defaultChecked, unlike the module switches below:
                        those are driven by a local useState, these by the
                        Formik value. Passing both makes React call the input
                        controlled and uncontrolled at once; `checked` would
                        win and the result would be the same, but by accident.
                      */}
                      <Switch
                        name={`callCenter.workingTime.${day}.open`}
                        label={t(label)}
                      />
                    </Col>
                    <Col md="3">
                      <Input
                        type="time"
                        name={`callCenter.workingTime.${day}.start`}
                        label={t('Opens at')}
                        disabled={!isOpen}
                      />
                    </Col>
                    <Col md="3">
                      <Input
                        type="time"
                        name={`callCenter.workingTime.${day}.end`}
                        label={t('Closes at')}
                        disabled={!isOpen}
                        subText={
                          day === 1
                            ? t(
                                'An appointment can be booked up to one minute before this time, so write the hour by which a call must be able to finish, not the hour the call center turns off the lights.',
                              )
                            : undefined
                        }
                      />
                    </Col>
                  </Row>
                );
              })}
              <Row>
                <Col md="6">
                  <Input
                    type="textarea"
                    name="callCenter.recurringClosedDays"
                    label={t('Closed every year')}
                    rows="4"
                    subText={t(
                      'One MM-DD per line, for the days that are closed every single year: 01-01, 12-25. Written once, they never expire.',
                    )}
                  />
                </Col>
                <Col md="6">
                  <Input
                    type="textarea"
                    name="callCenter.closedDays"
                    label={t('Closed on these dates')}
                    rows="4"
                    subText={t(
                      'One YYYY-MM-DD per line, for what changes every year: Easter Monday, bridge days, company holidays.',
                    )}
                  />
                </Col>
              </Row>
              <Row>
                <Col md="2">
                  <Switch
                    name="facebookModule"
                    label="Facebook Module"
                    defaultChecked={facebookModule}
                    onClick={() => setFacebookModule(!facebookModule)}
                  />
                </Col>
                <Col md="2">
                  <Switch
                    name="googleModule"
                    label="Google Module"
                    defaultChecked={googleModule}
                    onClick={() => setGoogleModule(!googleModule)}
                  />
                </Col>
                <Col md="2">
                  <Switch
                    name="tiktokModule"
                    label="TikTok Module"
                    defaultChecked={tiktokModule}
                    onClick={() => setTiktokModule(!tiktokModule)}
                  />
                </Col>
                <Col md="2">
                  <Switch
                    name="supplierModule"
                    label="Supplier Module"
                    defaultChecked={supplierModule}
                    onClick={() => setSupplierModule(!supplierModule)}
                  />
                </Col>
                <Col md="2">
                  <Switch
                    name="demModule"
                    label="DEM Module"
                    defaultChecked={demModule}
                    onClick={() => setDemModule(!demModule)}
                  />
                </Col>
                <Col md="2">
                  <Switch
                    name="rcsModule"
                    label="RCS Module"
                    defaultChecked={rcsModule}
                    onClick={() => setRcsModule(!rcsModule)}
                  />
                </Col>
                <Col md="2">
                  <Switch
                    name="aiModule"
                    label="AI Module"
                    defaultChecked={aiModule}
                    onClick={() => setAiModule(!aiModule)}
                  />
                </Col>
              </Row>
              <Collapse isOpen={facebookModule}>
                <Row>
                  <Col md="6">
                    <FieldArray
                      name="facebookAdAccountIds"
                      label="Facebook Ad Account Ids"
                      values={values}
                      defaultItem={{ accountId: '', datasetId: '' }}
                      fields={[
                        { name: 'accountId', label: 'Account ID', col: 5 },
                        { name: 'datasetId', label: 'Dataset ID', col: 5 },
                      ]}
                    />
                  </Col>
                  <Col md="6">
                    <Input
                      type="textarea"
                      name="facebookAccessToken"
                      id="facebookAccessToken"
                      label="Facebook Access Token"
                    />
                  </Col>
                </Row>
              </Collapse>
              <Collapse isOpen={tiktokModule}>
                <Row>
                  <Col md="6">
                    <FieldArray
                      name="tiktokAdAccountIds"
                      label="TikTok Ad Account Ids"
                      values={values}
                      defaultItem={{ accountId: '' }}
                      fields={[
                        {
                          name: 'accountId',
                          label: 'Advertiser ID',
                          col: 10,
                        },
                      ]}
                    />
                  </Col>
                  <Col md="6">
                    <Input
                      type="textarea"
                      name="tiktokAccessToken"
                      id="tiktokAccessToken"
                      label="TikTok Access Token"
                      subText={t(
                        'The access token of the TikTok developer app, authorized by these advertisers: one token covers all of them.',
                      )}
                    />
                  </Col>
                </Row>
              </Collapse>
              <Collapse isOpen={aiModule}>
                <Row>
                  <Col md="6">
                    <Input
                      type="text"
                      name="zernioAccountId"
                      id="zernioAccountId"
                      label="Zernio Account ID"
                      // Conditional, and not a bare `required`: Collapse
                      // keeps its children MOUNTED and Bootstrap merely
                      // hides them, so an always-required field would sit in
                      // the form empty and invisible whenever the module is
                      // off. @availity/form sets no noValidate (it is Formik
                      // over reactstrap, which renders a bare form), so the
                      // browser would refuse to submit and could not even
                      // focus the field to say why: saving ANY company with
                      // the module off would silently do nothing. Same shape
                      // as the fields under aiEnabled in
                      // MonetizationOrders/FormMonetizationOrder.
                      required={aiModule}
                      subText={t(
                        'The account of the WhatsApp number this company writes from, one per company: the engine reads it when it routes a lead and refuses to start a conversation without it. The agent opening templates must be approved on this very number — a template approved for another number does not exist for this one.',
                      )}
                    />
                  </Col>
                </Row>
              </Collapse>
              <div className="modal-footer">
                <SubmitButton
                  disabled={!isValid || props.loading}
                  create={props.create}
                />
              </div>
            </>
          )}
        </Form>
      </CardBody>
    </Card>
  );
};

export default FormCompany;
